import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  LinearProgress,
  Button,
  useTheme
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';

const SubscriptionStatus = ({ subscription, plan }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState('');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (subscription && subscription.expiresAt) {
      const updateTimeLeft = () => {
        const now = new Date();
        const expiresAt = new Date(subscription.expiresAt);
        const diff = expiresAt - now;

        if (diff <= 0) {
          setTimeLeft('Expirado');
          setProgress(100);
          return;
        }

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        setTimeLeft(`${days}d ${hours}h ${minutes}m`);

        // Calcular progresso
        const totalDuration = plan.duration * 24 * 60 * 60 * 1000; // duração total em milissegundos
        const elapsed = totalDuration - diff;
        const progressPercentage = (elapsed / totalDuration) * 100;
        setProgress(Math.min(progressPercentage, 100));
      };

      updateTimeLeft();
      const interval = setInterval(updateTimeLeft, 60000); // Atualiza a cada minuto

      return () => clearInterval(interval);
    }
  }, [subscription, plan]);

  const getStatusColor = () => {
    if (!subscription) return 'error';
    if (subscription.status === 'active') return 'success';
    if (subscription.status === 'expired') return 'error';
    return 'warning';
  };

  const getStatusText = () => {
    if (!subscription) return 'Sem assinatura';
    if (subscription.status === 'active') return 'Ativo';
    if (subscription.status === 'expired') return 'Expirado';
    return 'Pendente';
  };

  const getStatusIcon = () => {
    if (!subscription) return <WarningIcon />;
    if (subscription.status === 'active') return <CheckCircleIcon />;
    if (subscription.status === 'expired') return <WarningIcon />;
    return <AccessTimeIcon />;
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2">
            Status da Assinatura
          </Typography>
          <Chip
            icon={getStatusIcon()}
            label={getStatusText()}
            color={getStatusColor()}
            variant="outlined"
          />
        </Box>

        {subscription ? (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" gutterBottom>
                Plano: {plan.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Início: {new Date(subscription.startDate).toLocaleDateString('pt-BR')}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Expira em: {new Date(subscription.expiresAt).toLocaleDateString('pt-BR')}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Tempo restante:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTimeIcon color="primary" />
                <Typography variant="h6" color="primary">
                  {timeLeft}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Progresso do período:
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: theme.palette.grey[200],
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                  },
                }}
              />
            </Box>
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Você ainda não possui uma assinatura ativa
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/plans')}
              sx={{ mt: 2 }}
            >
              Ver Planos Disponíveis
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default SubscriptionStatus; 